/**
 * Waiting-list questionnaire — the four single-choice questions and their
 * option labels, verbatim from Figma (MVP-DESIGNES 9141-2939 .. 9220-3850).
 *
 * The labels double as the stored values: the backend
 * (`optuneutils.parameter_validators.platform_access_params.WAITLIST_QUESTION_OPTIONS`)
 * validates each answer against the exact same strings, so keep both lists in
 * sync when a question changes.
 *
 * Options are listed in Figma reading order (row by row across the two
 * columns); `splitIntoColumns` turns that into the column-first layout the
 * frames use.
 */

export const WAITLIST_QUESTIONS = [
    {
        key: 'role',
        question: 'What is your role?',
        options: [
            'Developer',
            'AI Researcher',
            'DevOPS',
            'VP R&D',
            'Operations',
            'Founder or Leadership',
            'Quality Assurance',
            'Other',
        ],
    },
    {
        key: 'industry',
        question: 'What industry are you in?',
        options: [
            'Cyber Security',
            'Healthcare & Life Sciences',
            'Fintech & Banking',
            'Legal & Compliance',
            'Automotive & Autonomous Systems',
            'Telecommunications',
            'Energy & Utilities',
            'Other',
        ],
    },
    {
        key: 'company_size',
        question: 'What is your company size?',
        options: ['Just me', '2-9', '10-49', '50-249', '250-999', '1,000+'],
    },
    {
        key: 'heard_about',
        question: 'How did you hear about us?',
        options: [
            'Friend or colleague',
            'Newsletter or blog post',
            'Google or other search',
            'Podcast',
            'AI tool (LLM)',
            'LinkedIn',
            'Instagram or Facebook',
            'Youtube',
            'Twitter/X',
            'Other',
        ],
    },
];

export const WAITLIST_STEP_COUNT = WAITLIST_QUESTIONS.length;

/** Column labels shown in the god-only Waiting List table, keyed by answer field. */
export const WAITLIST_ANSWER_LABELS = {
    role: 'Role',
    industry: 'Industry',
    company_size: 'Company size',
    heard_about: 'Heard about us',
};

/** Empty answer set: every question unanswered (`null` = skipped). */
export const emptyWaitlistAnswers = () =>
    Object.fromEntries(WAITLIST_QUESTIONS.map(({ key }) => [key, null]));

/**
 * Split a reading-order option list into two columns the way Figma lays the
 * tiles out: options alternate left / right, so the left column holds the even
 * indexes and the right column the odd ones.
 */
export const splitIntoColumns = (options) => [
    options.filter((_, index) => index % 2 === 0),
    options.filter((_, index) => index % 2 === 1),
];

/** Mirrors the backend regex: one "@" with text on both sides and a dotted domain. */
export const WAITLIST_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidWaitlistEmail = (value) => WAITLIST_EMAIL_PATTERN.test(String(value || '').trim());
